<template>
  <div class="py-4 container-fluid">
    <div class="row" v-if="cotizacion">
      <div class="col-lg-8">
        <div class="card mb-2">
          <div class="card-header pb-0">
            <div class="d-flex align-items-center">
              <p class="mb-0">Cotización #{{ cotizacion.id }}</p>
              <span class="badge badge-sm ms-2"
                :class="{
                  'bg-gradient-warning': cotizacion.status === 'pendiente',
                  'bg-gradient-success': cotizacion.status === 'convertida' && cotizacion.type !== 'traslado',
                  'bg-gradient-info': cotizacion.status === 'convertida' && cotizacion.type === 'traslado',
                  'bg-gradient-secondary': cotizacion.status === 'cancelada',
                }">
                {{ statusLabel }}
              </span>
              <argon-button color="secondary" size="sm" class="ms-auto" @click="$router.push('/cotizaciones')">
                Volver
              </argon-button>
            </div>
          </div>
          <div class="card-body">
            <p class="text-uppercase text-sm">Información del cliente</p>
            <div class="row">
              <div class="col-md-6">
                <label class="form-control-label">Nombre</label>
                <p class="text-sm mb-3"><strong>{{ cotizacion.name || '—' }}</strong></p>
              </div>
              <div class="col-md-6">
                <label class="form-control-label">Teléfono</label>
                <p class="text-sm mb-3">{{ cotizacion.phone || '—' }}</p>
              </div>
              <div class="col-md-6">
                <label class="form-control-label">Entrega</label>
                <p class="text-sm mb-3">
                  {{ cotizacion.delivery_type === 'domicilio' ? 'Domicilio' : 'Para llevar / Recoger' }}
                </p>
              </div>
              <div class="col-md-6" v-if="cotizacion.address">
                <label class="form-control-label">Dirección</label>
                <p class="text-sm mb-3">{{ cotizacion.address }}</p>
              </div>
              <div class="col-md-12" v-if="cotizacion.observation">
                <label class="form-control-label">Observación</label>
                <p class="text-sm mb-3">{{ cotizacion.observation }}</p>
              </div>
              <div class="col-md-6">
                <label class="form-control-label">Vendedor</label>
                <p class="text-sm mb-3">{{ cotizacion.seller?.name || '—' }}</p>
              </div>
              <div class="col-md-6">
                <label class="form-control-label">Fecha</label>
                <p class="text-sm mb-3">{{ formatDate(cotizacion.created_at) }}</p>
              </div>
            </div>

            <hr class="horizontal dark" />
            <div class="d-flex align-items-center mb-2">
              <p class="text-uppercase text-sm mb-0">Productos</p>
              <button type="button" class="btn btn-sm btn-outline-secondary ms-auto mb-0"
                title="Copiar ítems para Excel" @click="copyDetailsForExcel">
                <i class="fas fa-copy"></i>
              </button>
            </div>
            <div class="table-responsive">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Producto</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Cantidad</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Precio</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="detail in cotizacion.details" :key="detail.id">
                    <td>
                      <div class="d-flex flex-column px-2 py-1">
                        <h6 class="mb-0 text-sm">{{ detail.inventory?.name || 'Producto eliminado' }}</h6>
                        <p class="text-xs text-secondary mb-0">{{ detail.inventory?.ref || '' }}</p>
                      </div>
                    </td>
                    <td class="align-middle text-center text-sm">{{ formatQty(detail.quantity) }}</td>
                    <td class="align-middle text-center text-sm">{{ formatCurrency(detail.price) }}</td>
                    <td class="align-middle text-center text-sm">{{ formatCurrency(detail.total) }}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr>
                    <td colspan="3" class="text-end text-sm font-weight-bold">Total</td>
                    <td class="text-center text-sm font-weight-bold">{{ formatCurrency(cotizacion.total) }}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div class="col-lg-4">
        <div class="card mb-2" v-if="cotizacion.status === 'pendiente'">
          <div class="card-header pb-0">
            <p class="mb-0 text-uppercase text-sm">Convertir cotización</p>
          </div>
          <div class="card-body">
            <label class="form-control-label">Cliente de la venta</label>
            <div class="d-flex gap-2 mb-2">
              <div class="flex-grow-1 cotizacion-client-select-wrap">
                <select ref="clientSelect" class="form-select form-select-sm select2" style="width: 100%"></select>
              </div>
              <button type="button" class="btn btn-success btn-sm px-3" title="Nuevo cliente"
                @click="openClientModal">
                <i class="fas fa-plus"></i>
              </button>
            </div>
            <p class="text-xs text-muted mb-3">
              Si no selecciona uno, se buscará o creará automáticamente con el teléfono.
            </p>

            <label class="form-control-label">Tipo de factura</label>
            <select v-model="selectedTipoDoc" class="form-select form-select-sm mb-3">
              <option value="00FE">Factura de venta (00FE)</option>
              <option value="OS">Factura de venta (OS)</option>
            </select>

            <button type="button" class="btn btn-success w-100 mb-2"
              :disabled="isConverting" @click="convertToRemission('venta')">
              <i class="fas fa-cash-register me-1"></i>
              {{ isConverting ? 'Convirtiendo...' : 'Convertir en venta' }}
            </button>
            <button type="button" class="btn btn-info w-100"
              :disabled="isConverting" @click="convertToRemission('traslado')">
              <i class="fas fa-truck-moving me-1"></i>
              {{ isConverting ? 'Convirtiendo...' : 'Convertir en traslado' }}
            </button>
          </div>
        </div>

        <div class="card mb-2" v-if="cotizacion.status === 'convertida'">
          <div class="card-header pb-0">
            <p class="mb-0 text-uppercase text-sm">Conversión</p>
          </div>
          <div class="card-body">
            <p class="text-sm mb-1">
              Convertida en <strong>{{ cotizacion.type === 'traslado' ? 'Traslado' : 'Venta' }}</strong>
            </p>
            <p v-if="cotizacion.remission_id" class="text-sm mb-0">
              Remisión #{{ cotizacion.remission_id }}
            </p>
            <p v-if="cotizacion.client" class="text-xs text-secondary mb-0">
              Cliente: {{ cotizacion.client.name }}
            </p>
          </div>
        </div>

        <div class="card mb-2" v-if="cotizacion.status !== 'convertida'">
          <div class="card-body">
            <button type="button" class="btn btn-outline-danger w-100 btn-sm" @click="removeCotizacion">
              <i class="fas fa-trash me-1"></i> Eliminar cotización
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="row" v-else>
      <div class="col-12">
        <div class="card"><div class="card-body text-center text-sm py-4">Cargando cotización...</div></div>
      </div>
    </div>

    <!-- Modal nuevo cliente (igual que POS) -->
    <div class="modal fade" id="cotizacionClientModal" tabindex="-1" ref="clientModal">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Nuevo cliente</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <label class="form-control-label">Nombre <span class="text-danger">*</span></label>
            <input v-model="newClient.name" type="text" class="form-control mb-3" />
            <label class="form-control-label">Documento</label>
            <input v-model="newClient.document" type="text" class="form-control mb-3" />
            <label class="form-control-label">Correo</label>
            <input v-model="newClient.email" type="email" class="form-control mb-1" placeholder="Opcional" />
            <p class="text-muted text-xs mb-3">Si no indica correo, se usará cliente+hash@mistock.co</p>
            <label class="form-control-label">Teléfono</label>
            <input v-model="newClient.phone" type="tel" class="form-control mb-3" placeholder="Opcional" />
            <label class="form-control-label">Departamento</label>
            <select v-model="newClient.departamento" class="form-select mb-3" @change="onClientDepartamentoChange">
              <option value="">Seleccione (opcional)</option>
              <option v-for="dep in departamentos" :key="dep" :value="dep">{{ dep }}</option>
            </select>
            <label class="form-control-label">Municipio</label>
            <select v-model="newClient.municipio" class="form-select mb-3" :disabled="!newClient.departamento">
              <option value="">Seleccione (opcional)</option>
              <option v-for="mun in clientMunicipios" :key="mun" :value="mun">{{ mun }}</option>
            </select>
            <label class="form-control-label">Código Postal</label>
            <input v-model="newClient.codigo_postal" type="text" class="form-control mb-0" placeholder="Opcional" />
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancelar</button>
            <button type="button" class="btn btn-success" @click="saveClient">Guardar</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import cotizacionService from "@/services/cotizacion.service";
import posService from "@/services/pos.service";
import ArgonButton from "@/components/ArgonButton.vue";
import { Modal } from "bootstrap";
import Swal from "sweetalert2";
import $ from "jquery";
import "select2/dist/css/select2.css";
import "select2";
import { DEPARTAMENTOS, getMunicipios } from "@/utils/colombiaLocations";

export default {
  name: "Cotizaciones Detail",
  components: { ArgonButton },
  data() {
    return {
      cotizacion: null,
      isConverting: false,
      clients: [],
      selectedClientId: null,
      selectedTipoDoc: "00FE",
      clientModal: null,
      newClient: { name: "", document: "", email: "", phone: "", departamento: "", municipio: "", codigo_postal: "" },
      departamentos: DEPARTAMENTOS,
      clientMunicipios: [],
    };
  },
  computed: {
    statusLabel() {
      if (!this.cotizacion) return "";
      if (this.cotizacion.status === "convertida") {
        return this.cotizacion.type === "traslado" ? "Convertida → Traslado" : "Convertida → Venta";
      }
      const labels = { pendiente: "Pendiente", cancelada: "Cancelada" };
      return labels[this.cotizacion.status] || this.cotizacion.status;
    },
  },
  methods: {
    async loadCotizacion() {
      const id = this.$route.query.id;
      if (!id) {
        this.$router.push("/cotizaciones");
        return;
      }
      try {
        const res = await cotizacionService.get(id);
        this.cotizacion = res.data;
        this.selectedClientId = this.cotizacion.user_id || null;
        if (this.cotizacion.status === "pendiente") {
          await this.loadClients(this.cotizacion.user_id || null);
        }
      } catch (error) {
        console.error("Error al cargar la cotización:", error);
        Swal.fire("Error", "No se pudo cargar la cotización.", "error");
      }
    },
    async loadClients(selectId = null) {
      try {
        const res = await posService.getClients();
        this.clients = res.data || [];
        if (!selectId && this.cotizacion?.phone) {
          const match = this.clients.find((c) => c.phone === this.cotizacion.phone);
          if (match) selectId = match.id;
        }
        await this.$nextTick();
        this.initClientSelect2(selectId);
      } catch (e) {
        console.error("Error al cargar clientes:", e);
        this.clients = [];
      }
    },
    destroyClientSelect2() {
      const select = this.$refs.clientSelect;
      if (!select) return;
      const $select = $(select);
      if ($select.hasClass("select2-hidden-accessible")) {
        $select.off("change");
        $select.select2("destroy");
      }
    },
    initClientSelect2(selectId = null) {
      const select = this.$refs.clientSelect;
      if (!select) return;

      const $select = $(select);
      this.destroyClientSelect2();

      $select.empty();
      $select.append('<option value="">Seleccionar cliente</option>');

      this.clients.forEach((client) => {
        const doc = client.document || client.id;
        $select.append(new Option(`${doc} - ${client.name}`, client.id, false, false));
      });

      $select.select2({
        placeholder: "Seleccione un cliente",
        allowClear: true,
        width: "100%",
        dropdownParent: $(this.$el).find(".cotizacion-client-select-wrap"),
      });

      $select.on("change", () => {
        const id = $select.val();
        this.selectedClientId = id ? Number(id) : null;
      });

      const idToSet = selectId ?? this.selectedClientId;
      if (idToSet) {
        $select.val(String(idToSet)).trigger("change");
      }
    },
    openClientModal() {
      this.newClient = { name: "", document: "", email: "", phone: "", departamento: "", municipio: "", codigo_postal: "" };
      if (this.cotizacion?.name) this.newClient.name = this.cotizacion.name;
      if (this.cotizacion?.phone) this.newClient.phone = this.cotizacion.phone;
      this.clientMunicipios = [];
      if (!this.clientModal) {
        this.clientModal = new Modal(this.$refs.clientModal);
      }
      this.clientModal.show();
    },
    onClientDepartamentoChange() {
      this.newClient.municipio = "";
      this.clientMunicipios = getMunicipios(this.newClient.departamento);
    },
    async saveClient() {
      if (!this.newClient.name.trim()) {
        Swal.fire("Nombre requerido", "Indique el nombre del cliente.", "warning");
        return;
      }
      try {
        const payload = {
          name: this.newClient.name.trim(),
          document: this.newClient.document?.trim() || null,
          email: this.newClient.email?.trim() || null,
          phone: this.newClient.phone?.trim() || null,
          departamento: this.newClient.departamento || null,
          municipio: this.newClient.municipio || null,
          codigo_postal: this.newClient.codigo_postal?.trim() || null,
        };
        const res = await posService.createClient(payload);
        this.clientModal.hide();
        await this.loadClients(res.data.id);
        Swal.fire("Cliente creado", res.data.email ? `Correo: ${res.data.email}` : "", "success");
      } catch (e) {
        const msg =
          e?.response?.data?.message ||
          (e?.response?.data?.errors
            ? Object.values(e.response.data.errors).flat().join(" ")
            : null) ||
          "No se pudo guardar el cliente.";
        Swal.fire("Error", msg, "error");
      }
    },
    async convertToRemission(conversionType = "venta") {
      const label = conversionType === "traslado" ? "traslado" : "venta";
      const confirm = await Swal.fire({
        icon: "question",
        title: `Convertir en ${label}`,
        text: `¿Convertir la cotización #${this.cotizacion.id} en ${label}?`,
        showCancelButton: true,
        confirmButtonText: "Sí, convertir",
        cancelButtonText: "Cancelar",
      });
      if (!confirm.isConfirmed) return;

      this.isConverting = true;
      try {
        const res = await cotizacionService.convert(
          this.cotizacion.id,
          this.selectedClientId,
          conversionType,
          this.selectedTipoDoc
        );
        await Swal.fire("Convertida", res.data?.message || "La cotización fue convertida.", "success");
        await this.loadCotizacion();
      } catch (error) {
        const msg = error?.response?.data?.message || "No se pudo convertir la cotización.";
        Swal.fire("Error", msg, "error");
      } finally {
        this.isConverting = false;
      }
    },
    async removeCotizacion() {
      const confirm = await Swal.fire({
        icon: "warning",
        title: "Eliminar cotización",
        text: `¿Eliminar la cotización #${this.cotizacion.id}?`,
        showCancelButton: true,
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      });
      if (!confirm.isConfirmed) return;

      try {
        await cotizacionService.remove(this.cotizacion.id);
        this.$router.push("/cotizaciones");
      } catch (error) {
        const msg = error?.response?.data?.message || "No se pudo eliminar la cotización.";
        Swal.fire("Error", msg, "error");
      }
    },
    formatExcelCurrency(value) {
      const n = parseFloat(value) || 0;
      return n.toLocaleString('es-CO',
        { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    escapeExcelCell(raw) {
      if (raw == null || raw === '') return '';
      return String(raw).replace(/\t/g, ' ')
        .replace(/\r?\n/g, ' ').trim();
    },
    buildExportHeader() {
      return ['Producto', 'Cantidad'].join('\t');
    },
    buildExportRow(value) {
      const cells = [
        this.escapeExcelCell(value?.inventory?.ref ?? ''),
        this.escapeExcelCell(this.formatQty(value.quantity)),
      ];
      return cells.join('\t');
    },
    async copyDetailsForExcel() {
      const rows = this.cotizacion?.details;
      if (!Array.isArray(rows) || rows.length === 0) {
        await Swal.fire({ icon: 'info', title: 'Sin ítems',
          text: 'No hay líneas para copiar.',
          confirmButtonText: 'Aceptar' });
        return;
      }
      const tsv = [this.buildExportHeader(),
        ...rows.map((r) => this.buildExportRow(r))].join('\n');
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(tsv);
        } else {
          const ta = document.createElement('textarea');
          ta.value = tsv;
          ta.style.position = 'fixed';
          ta.style.left = '-9999px';
          document.body.appendChild(ta);
          ta.focus(); ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
        }
        await Swal.fire({ icon: 'success', title: 'Copiado',
          text: 'Datos listos para pegar en Excel (Ctrl+V).',
          timer: 2200, showConfirmButton: false });
      } catch (e) {
        await Swal.fire({ icon: 'error', title: 'No se pudo copiar',
          text: 'El navegador bloqueó el portapapeles o no hay permisos HTTPS.',
          confirmButtonText: 'Aceptar' });
      }
    },
    formatCurrency(value) {
      return (Number(value) || 0).toLocaleString("es-CO", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    },
    formatQty(value) {
      const n = parseFloat(value) || 0;
      return Number.isInteger(n) ? n : n.toFixed(2);
    },
    formatDate(value) {
      if (!value) return "—";
      return new Date(value).toLocaleString("es-CO", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
  mounted() {
    this.loadCotizacion();
  },
  beforeUnmount() {
    this.destroyClientSelect2();
  },
};
</script>
